export type RangeBounds = { min: number | null; max: number | null };

/** Parse one numeric input; blank or non-finite becomes `null` (= unbounded). */
export const parseBound = (value: string): number | null => {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Parse a draft min/max pair, swapping inverted bounds so entering "500 – 100"
 * filters 100–500 instead of silently matching nothing.
 */
export const normalizeRange = (min: string, max: string): RangeBounds => {
  const lo = parseBound(min);
  const hi = parseBound(max);
  return lo != null && hi != null && lo > hi
    ? { min: hi, max: lo }
    : { min: lo, max: hi };
};

export const rangesEqual = (left: RangeBounds, right: RangeBounds) =>
  left.min === right.min && left.max === right.max;

// ponytail: inline self-check for the parse/swap branches. Dev-only; a
// mismatch throws loudly on import.
if (process.env.NODE_ENV === "development") {
  const expect = (actual: RangeBounds, wanted: RangeBounds, msg: string) => {
    if (!rangesEqual(actual, wanted)) {
      throw new Error(`normalizeRange: ${msg} => ${JSON.stringify(actual)}`);
    }
  };

  expect(
    normalizeRange("", ""),
    { min: null, max: null },
    "blank is unbounded"
  );
  expect(normalizeRange("100", "500"), { min: 100, max: 500 }, "ordered kept");
  expect(
    normalizeRange("500", "100"),
    { min: 100, max: 500 },
    "inverted swapped"
  );
  expect(
    normalizeRange("", "500"),
    { min: null, max: 500 },
    "open lower bound"
  );
  expect(
    normalizeRange("abc", "500"),
    { min: null, max: 500 },
    "invalid is null"
  );
}
